import { produce } from "immer";
import React, { useState, useEffect, useContext } from "react";
import { DndProvider } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";

import { exists } from "@examspark/common-util";
import { Stem, StemMatch } from "@examspark/types";
import { Pair } from "@examspark/types/dist/item-stem";

import { hideTokenAfterUse } from "../common/drag/dndUtils";
import QuestionContext from "../QuestionContext";

import MatchPair from "./common/MatchPair";
import Prompt from "./shared/Prompt";
import Token, { DndItem } from "./shared/Token";

// todo: fix all of the any's caused by data being unknown
type MatchProps = {
  data: StemMatch;
};
const Match = (props: MatchProps) => {
  const [show, setShow] = useState(false);
  const [player, dispatchPlayer] = useContext(QuestionContext);

  /**
   * Hack: DndProvider doens't like it when you unmount one provider and add a
   * new one in rapid fashion. Do this provided a little window to let the
   * providers move in/out without colliding.
   */
  useEffect(() => {
    setTimeout(() => {
      setShow(true);
    }, 25);
  }, []);

  // Reveal Key
  useEffect(() => {
    if (player.revealKey) {
      const keys: Stem.Pair[] = [];
      props.data.scoring?.pairs?.forEach((p) => {
        keys.push({
          src: p.src,
          dst: p.dst,
        });
      });

      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          score: true,
          response: {
            pairs: keys,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.revealKey]);

  if (!show) {
    return null;
  }

  const pairs: Pair[] = player.response?.[props.data.id]?.pairs || [];

  // coerce the necessary shape for hideTokensAfterUse to understand
  const leftSide = pairs
    .map((p) => p.src)
    .filter(exists)
    .map((src) => ({
      draggableId: src,
    }));
  const rightSide = pairs
    .map((p) => p.dst)
    .filter(exists)
    .map((dst) => ({
      draggableId: dst,
    }));

  const filteredTokensOne = hideTokenAfterUse(
    props.data.content.optionsOne.map((o) => ({
      type: "text",
      item: o,
    })),
    leftSide,
  );
  const filteredTokensTwo = hideTokenAfterUse(
    props.data.content.optionsTwo.map((o) => ({
      type: "text",
      item: o,
    })),
    rightSide,
  );

  return (
    <div className="item-player-element item-player-stem">
      <Prompt data={props.data.content.prompt} />
      {/* @ts-expect-error children */}
      <DndProvider
        backend={TouchBackend}
        options={{
          enableMouseEvents: true,
          enableHoverOutsideTarget: true,
        }}
        context={window}
      >
        {props.data.scoring?.pairs && (
          <div className="item-player-matches">
            {props.data.scoring.pairs &&
              props.data.scoring.pairs.map((p, idx) => {
                const rsp = pairs.at(idx);

                return (
                  <MatchPair
                    key={idx}
                    acceptLeft={["token:pool1"]}
                    acceptRight={["token:pool2"]}
                    response={rsp}
                    pairs={props.data.scoring?.pairs}
                    // index={idx}
                    dataSrc={props.data.content.optionsOne}
                    dataDst={props.data.content.optionsTwo}
                    onDrop={(from: "src" | "dst", item: DndItem) => {
                      const rsp = produce(pairs, (draft) => {
                        if (!draft[idx]) {
                          draft[idx] = {};
                        }

                        draft[idx][from] = item.id;
                      });

                      dispatchPlayer({
                        type: "RESPONSE",
                        data: {
                          id: props.data.id,
                          response: {
                            pairs: rsp,
                          },
                        },
                      });
                    }}
                    onLeave={(side: "src" | "dst") => {
                      const next = produce(pairs, (draft) => {
                        draft[idx][side] = undefined;
                      });

                      dispatchPlayer({
                        type: "RESPONSE",
                        data: {
                          id: props.data.id,
                          response: {
                            pairs: next,
                          },
                        },
                      });
                    }}
                  />
                );
              })}
          </div>
        )}
        {props.data.content.optionsOne && (
          <div className="item-player-tokens">
            {filteredTokensOne.map((opt) => {
              return (
                <Token
                  key={opt.item.id}
                  data={opt.item}
                  useDragType="token:pool1"
                />
              );
            })}
          </div>
        )}
        {props.data.content.optionsTwo && (
          <div className="item-player-tokens">
            {filteredTokensTwo.map((opt) => {
              return (
                <Token
                  key={opt.item.id}
                  data={opt.item}
                  useDragType="token:pool2"
                />
              );
            })}
          </div>
        )}
      </DndProvider>
    </div>
  );
};
export default Match;
